import { useState } from "react";
import { motion } from "framer-motion";
import { Download, MapPin, ShieldCheck, Award } from "lucide-react";
import { Button } from "../ui/button";
import { LeadDialog } from "./LeadDialog";
import { AssetImage } from "./AssetImage";
import { ASSETS, HERO_BADGES, waLink, CONTACT } from "../../config/site";

const TrustBadge = ({ badge }) => {
  const [ok, setOk] = useState(true);
  const asset = badge.id === "govt" ? ASSETS.badgeHaryana : badge.id === "prime" ? ASSETS.badgeMsme : null;

  const chip = (
    <div
      className="flex items-center gap-3 border border-gold/40 bg-forest-dark/50 px-4 py-2.5 backdrop-blur-sm"
      data-testid={`hero-badge-${badge.id}`}
    >
      {badge.id === "nh" ? (
        <MapPin className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
      ) : badge.id === "govt" ? (
        <ShieldCheck className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
      ) : (
        <Award className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
      )}
      <div className="leading-tight">
        <p className="text-sm font-medium text-paper">{badge.label}</p>
        <p className="text-[11px] text-paper/60">{badge.sub}</p>
      </div>
    </div>
  );

  if (!asset) return chip;
  return ok ? (
    <div className="flex items-center gap-3 border border-gold/40 bg-forest-dark/50 px-4 py-2.5 backdrop-blur-sm">
      <img
        src={asset.local}
        onError={() => setOk(false)}
        alt={badge.label}
        className="h-10 w-auto max-w-[72px] object-contain"
        data-testid={`hero-badge-img-${badge.id}`}
      />
      <div className="leading-tight">
        <p className="text-sm font-medium text-paper">{badge.label}</p>
        <p className="text-[11px] text-paper/60">{badge.sub}</p>
      </div>
    </div>
  ) : (
    chip
  );
};

export const Hero = () => {
  return (
    <section id="top" data-testid="section-hero" className="relative flex min-h-[100svh] items-end overflow-hidden bg-forest-dark">
      <div className="absolute inset-0">
        <AssetImage
          asset={ASSETS.hero}
          alt="BIDC industrial estate at dusk, Bhiwani"
          className="h-full w-full object-cover"
          placeholderLabel="BIDC factory hero image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/70 to-forest-dark/40" />
        <div className="grain absolute inset-0" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-xs sm:text-sm uppercase tracking-[0.3em] font-medium text-gold"
        >
          Tirupati Developers · Bhiwani, Haryana
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08] tracking-tight text-paper"
          data-testid="hero-headline"
        >
          Bhiwani Industrial Development Cluster
          <span className="mt-3 block text-2xl sm:text-3xl lg:text-4xl font-normal italic text-gold">
            Where enterprises establish legacies
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-paper/75"
        >
          A Govt. of Haryana approved MSME industrial cluster on NH-709 — ready plots,
          plug &amp; play sheds, and an ecosystem that lets factories start faster.
          <span className="mt-2 block text-gold/90 font-display italic">जहाँ उद्योग बनते हैं विरासत।</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <LeadDialog type="site_visit">
            <Button
              size="lg"
              data-testid="hero-book-visit-button"
              className="bg-gold text-forest-dark hover:bg-paper hover:text-forest transition-colors duration-200 font-medium"
            >
              Book a Site Visit
            </Button>
          </LeadDialog>
          <LeadDialog type="brochure">
            <Button
              size="lg"
              variant="outline"
              data-testid="hero-brochure-button"
              className="border-paper/40 bg-transparent text-paper hover:bg-paper/10 hover:text-paper transition-colors duration-200"
            >
              <Download className="mr-2 h-4 w-4" /> Download Brochure
            </Button>
          </LeadDialog>
          <Button
            asChild
            size="lg"
            variant="outline"
            data-testid="hero-whatsapp-button"
            className="border-[#25D366]/50 bg-[#25D366]/10 text-[#7ee2a8] hover:bg-[#25D366]/20 hover:text-[#7ee2a8] transition-colors duration-200"
          >
            <a
              href={waLink(CONTACT.whatsappPrimary, "Namaste! I want to know more about BIDC plots.")}
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp Us
            </a>
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          {HERO_BADGES.map((b) => (
            <TrustBadge key={b.id} badge={b} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
